// components/helpers/ActionCardSection.tsx
import React, { JSX } from 'react';
import { Content } from '.generated/Feature.CW.model';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { SvgIcon, IconTypes } from 'helpers/SvgIcon';
import { tv } from 'tailwind-variants';
import useIsEditing from 'lib/hooks/useIsEditing';

type Props = {
  actionCardsList: Content.ConditionHeaderActionCard[];
};

const actionCardVariants = tv({
  slots: {
    cardSection: ['bg-white', 'rounded-md', 'shadow-default', 'border-gray-50'],
  },
});

const ActionCardSection = ({ actionCardsList }: Props): JSX.Element => {
  const { cardSection } = actionCardVariants();
  const isEditing = useIsEditing();

  const fullUrl = typeof window !== 'undefined' ? window.location.href : '';

  const IconMap: Record<string, IconTypes> = {
    ScheduleAnAppointment: 'calendar-blank',
    RequestAnAppointment: 'calendar-blank',
    Call: 'phone',
    ReferAPatient: 'user-plus',
    RequestASecondOpinion: 'chat',
    GetCareToday: 'stethoscope',
  };

  const getIcon = (cardType: string): IconTypes | undefined => IconMap[cardType];

  const getCardStyles = (cardType: string) => {
    switch (cardType) {
      case 'ScheduleAnAppointment':
      case 'RequestAnAppointment':
        return {
          bg: 'bg-white group-hover:bg-blue-50',
          iconBg: 'bg-blue-50',
          icon: '[&>path]:fill-blue-700',
        };
      case 'Call':
      case 'GetCareToday':
        return {
          bg: 'bg-white group-hover:bg-purple-50',
          iconBg: 'bg-purple-50',
          icon: '[&>path]:fill-purple-700',
        };
      case 'ReferAPatient':
        return {
          bg: 'bg-white group-hover:bg-green-50',
          iconBg: 'bg-green-50',
          icon: '[&>path]:fill-green-800',
        };
      case 'RequestASecondOpinion':
        return {
          bg: 'bg-white group-hover:bg-orange-50',
          iconBg: 'bg-orange-50',
          icon: '[&>path]:fill-orange-700',
        };
      default:
        return {
          bg: 'bg-white',
          iconBg: 'bg-white',
          icon: '[&>path]:fill-gray-500',
        };
    }
  };

  return (
    <div className={cardSection()}>
      <ul className="grid grid-cols-1 lg:grid-flow-col lg:auto-cols-fr md:grid-rows-1 lg:py-0 py-8">
        {actionCardsList?.map((cardItem: Content.ConditionHeaderActionCard, index: number) => {
          const cardType = cardItem?.fields?.cardType?.value;
          const cardStyle = getCardStyles(cardType || '');

          const link =
            cardType === 'RequestAnAppointment'
              ? {
                  value: {
                    ...cardItem?.fields?.link?.value,
                    href:
                      cardItem?.fields?.link?.value?.linktype === 'internal'
                        ? `${cardItem?.fields?.link?.value?.href}${
                            cardItem?.fields?.link?.value?.href?.includes('?') ? '&' : '?'
                          }pageUrl=${fullUrl}`
                        : cardItem?.fields?.link?.value?.href,
                  },
                }
              : cardItem?.fields?.link;
          return (
            <li
              key={index}
              className="group cursor-pointer relative after:content-[''] after:absolute lg:after:top-[16px] lg:after:bottom-[16px] lg:after:right-0 after:bottom-0 after:left-[16px] lg:after:left-[unset] after:right-[16px] lg:after:w-[2px] after:w-[calc(100%-32px)] after:h-[2px] lg:after:h-[calc(100%-32px)] after:bg-gray-50 md:after:block last:after:content-none md:border-r-transparent first:rounded-tl-md first:rounded-bl-md last:rounded-tr-md last:rounded-br-md overflow-hidden lg:p-8 py-0 px-8"
            >
              {/* Show only in Editing mode to edit link */}
              {isEditing ? <LinkWrapper field={cardItem?.fields?.link} className="py-4" /> : <></>}
              <LinkWrapper className="cardItem w-full block lg:h-full" field={link}>
                <div
                  className={`flex lg:h-full rounded-md lg:flex-col flex-row items-center lg:py-8 py-11 lg:pr-0 pr-4 ${cardStyle.bg}`}
                >
                  {cardType && (
                    <span
                      className={`icon-rounded flex justify-center items-center rounded-full p-7 lg:h-[70px] lg:w-[70px] h-[48px] w-[48px] bg-blue-50 text-center lg:mb-8 lg:mr-0 mr-8 lg:ml-0 ml-4 group-hover:bg-white ${cardStyle.iconBg}`}
                    >
                      <SvgIcon
                        fill="none"
                        icon={getIcon(cardType)}
                        size="custom"
                        aria-label="icon"
                        className={`lg:!h-[36px] lg:!w-[36px] !h-[20px] !w-[20px] ${cardStyle.icon}`}
                      />
                    </span>
                  )}
                  <PlainTextWrapper
                    tag="span"
                    className="font-centuryGothic lg:text-center text-left text-18 font-bold text-gray-900 group-hover:underline"
                    field={cardItem?.fields?.title}
                  />
                </div>
              </LinkWrapper>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ActionCardSection;
